import Client, { Client as ShopifyClient } from "shopify-buy";
import config from "../../apiConfig.ts";
import {
  ADD_LINES_MUTATION,
  CREATE_CART_MUTATION,
  REMOVE_LINES_MUTATION,
  UPDATE_LINES_MUTATION,
} from "../graphql/mutations/checkout.js";
import { GET_CART_QUERY } from "../graphql/queries/checkout.js";

const {
  storefrontAccessToken,
  domain,
  appId,
  storefrontAccessToken_1,
  domain_1,
  appId_1,
} = config;

const shopifyInit = (countryCode) => {
  let clientConfig;

  if (countryCode === "en-US") {
    clientConfig = {
      storefrontAccessToken: storefrontAccessToken_1,
      domain: domain_1,
      appId: appId_1,
    };
  } else {
    clientConfig = {
      storefrontAccessToken: storefrontAccessToken,
      domain: domain,
      appId: appId,
    };
  }
  return Client.buildClient(clientConfig);
};

const getShopifyClientConfig = (countryCode) => {
  let clientConfig;

  if (countryCode === "en-US") {
    clientConfig = {
      storefrontAccessToken: storefrontAccessToken_1,
      domain: domain_1,
      appId: appId_1,
    };
  } else {
    clientConfig = {
      storefrontAccessToken: storefrontAccessToken,
      domain: domain,
      appId: appId,
    };
  }

  return {
    domain: clientConfig.domain,
    storefrontAccessToken: clientConfig.storefrontAccessToken,
  };
};

const shopifyCheckoutService = async (client, body) => {
  try {
    if (client && client.domain && client.storefrontAccessToken) {
      const result = await fetch(
        `https://${client.domain}/api/2024-04/graphql.json`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Shopify-Storefront-Access-Token":
              client.storefrontAccessToken,
          },
          body: JSON.stringify(body),
        }
      );
      const data = await result.json();

      if (data.errors) {
        throw new Error(JSON.stringify(data.errors));
      }

      return data;
    }
    return {};
  } catch (err) {
    throw err;
  }
};

const getCheckoutId = (countryCode) => {
  const client = getShopifyClientConfig(countryCode);
  return new Promise(async (resolve, reject) => {
    if (countryCode) {
      let checkoutIdFromSession = sessionStorage.getItem(
        `${countryCode}-checkoutId`
      );

      if (
        checkoutIdFromSession === undefined ||
        checkoutIdFromSession === null
      ) {
        try {
          const createCartBody = {
            query: CREATE_CART_MUTATION,
            variables: { input: { lines: [] } },
          };

          const data = await shopifyCheckoutService(
            client,
            createCartBody
          );

          const cart = data?.data?.cartCreate?.cart;
          sessionStorage.setItem(
            `${countryCode}-checkoutId`,
            cart.id
          );
          resolve(cart.id);
        } catch (err) {
          console.error("Error creating cart:", err);
          reject(err);
        }
      } else {
        resolve(checkoutIdFromSession);
      }
    }
  });
};
const getAllProducts = (countryCode) => {
  const client = shopifyInit(countryCode);
  return new Promise((resolve, reject) => {
    client.product.fetchAll().then((products) => {
      // Do something with the products
      resolve(products);
    });
  });
};
const getProductDetails = (productId, countryCode) => {
  const client = shopifyInit(countryCode);
  return new Promise((resolve, reject) => {
    client.product
      .fetch(productId)
      .then((product) => {
        // Do something with the product

        resolve(product);
      })
      .catch((err) => {
        console.log("err", err);
      });
  });
};

const getCheckoutLineItems = (countryCode) => {
  const client = getShopifyClientConfig(countryCode);

  return new Promise((resolve, reject) => {
    getCheckoutId(countryCode)
      .then(async (cartId) => {
        try {
          const getCheckoutLineItemsBody = {
            query: GET_CART_QUERY,
            variables: { cartId },
          };

          const data = await shopifyCheckoutService(
            client,
            getCheckoutLineItemsBody
          );

          const cart = data?.data?.cart;
          const lineItems = cart.lines.edges.map((edge) => edge.node);
          resolve({ ...cart, lineItems });
        } catch (err) {
          console.error("Error getting checkout line items: ", err);
          reject(err);
        }
      })
      .catch((err) => reject(err));
  });
};

const setAddVariantToCart = (lineItemsToAdd, countryCode) => {
  const client = getShopifyClientConfig(countryCode); // { domain, storefrontAccessToken }

  return new Promise((resolve, reject) => {
    getCheckoutId(countryCode)
      .then(async (cartId) => {
        try {
          const setAddVariantToCartBody = {
            query: ADD_LINES_MUTATION,
            variables: {
              cartId,
              lines: lineItemsToAdd,
            },
          };

          const data = await shopifyCheckoutService(
            client,
            setAddVariantToCartBody
          );

          const cart = data?.data?.cartLinesAdd?.cart;
          const lineItems = cart.lines.edges.map((item) => item.node);
          resolve({ ...cart, lineItems });
        } catch (err) {
          console.error("Error adding variant to cart: ", err);
          sessionStorage.removeItem(`${countryCode}-checkoutId`);
          reject(err);
        }
      })
      .catch((err) => reject(err));
  });
};

const setUpdateQuantityInCart = (lineItemsToUpdate, countryCode) => {
  const client = getShopifyClientConfig(countryCode);

  return new Promise((resolve, reject) => {
    getCheckoutId(countryCode)
      .then(async (cartId) => {
        try {
          const setUpdateQuantityInCartBody = {
            query: UPDATE_LINES_MUTATION,
            variables: { cartId, lines: lineItemsToUpdate },
          };

          const data = await shopifyCheckoutService(
            client,
            setUpdateQuantityInCartBody
          );

          const cart = data?.data?.cartLinesUpdate?.cart;
          const lineItems = cart.lines.edges.map((item) => item.node);
          resolve({ ...cart, lineItems });
        } catch (err) {
          console.error("Error updating quantity to cart: ", err);
          reject(err);
        }
      })
      .catch((err) => reject(err));
  });
};

const setRemoveLineItemInCart = (lineItemId, countryCode) => {
  const client = getShopifyClientConfig(countryCode);

  return new Promise((resolve, reject) => {
    getCheckoutId(countryCode)
      .then(async (cartId) => {
        try {
          const setRemoveLineItemInCartBody = {
            query: REMOVE_LINES_MUTATION,
            variables: {
              cartId,
              lineIds: [lineItemId],
            },
          };

          const data = await shopifyCheckoutService(
            client,
            setRemoveLineItemInCartBody
          );

          const cart = data?.data?.cartLinesRemove?.cart;
          const lineItems = cart.lines.edges.map((item) => item.node);
          resolve({ ...cart, lineItems });
        } catch (err) {
          console.error("Error removing line items from cart: ", err);
          reject(err);
        }
      })
      .catch((err) => reject(err));
  });
};

export {
  shopifyInit,
  getAllProducts,
  getProductDetails,
  getCheckoutId,
  getCheckoutLineItems,
  setAddVariantToCart,
  setUpdateQuantityInCart,
  setRemoveLineItemInCart,
};
